import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Loader2, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GradeBadge } from "@/components/GradeBadge";
import { EASE_OUT } from "@/components/motion/Reveal";
import { getOrder, recheckOrder, createOrderCheckout } from "@/lib/api";
import { metaPurchaseBrowser } from "@/lib/meta";
import { formatPrice } from "@/lib/format";

const POLL_MS = 3000;
const MAX_POLLS = 20; // ~60s

export default function CheckoutConfirm() {
  const [params] = useSearchParams();
  // SumUp torna con ?ordine=NUMERO (manteniamo ?order per retrocompatibilità).
  const orderNumber = params.get("ordine") || params.get("order");
  const [order, setOrder] = useState(null);
  const [metaInfo, setMetaInfo] = useState(null);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!orderNumber) { setError(true); return; }
    let timer;
    let cancelled = false;

    const tick = async () => {
      try {
        // Lo stato REALE arriva sempre dal DB/SumUp: se ancora in attesa, il
        // backend rilegge la checkout via GET (mai fidarsi del redirect).
        let data;
        try {
          const res = await recheckOrder(orderNumber);
          data = res.order;
          if (res.meta) setMetaInfo(res.meta);
        } catch {
          data = await getOrder(orderNumber);
        }
        if (cancelled || !data) return;
        setOrder(data);
        const status = data.payment_status;
        if (status === "pending" && pollsRef.current < MAX_POLLS) {
          pollsRef.current += 1;
          timer = setTimeout(tick, POLL_MS);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [orderNumber]);

  // Meta Pixel — Purchase lato browser con lo STESSO event_id del server
  // (purchase_<ordine>), così Meta deduplica. Parte una sola volta per ordine, solo
  // a pagamento confermato dal DB e solo se c'è consenso marketing (pixel caricato).
  useEffect(() => {
    if (!order || order.payment_status !== "paid" || !metaInfo) return;
    const on = order.order_number;
    const key = `fb_purchase_tracked_${on}`;
    try { if (localStorage.getItem(key)) return; } catch { /* noop */ }
    metaPurchaseBrowser({
      eventId: metaInfo.event_id,
      value: metaInfo.value,
      currency: metaInfo.currency || "EUR",
      contentIds: metaInfo.content_ids,
      numItems: metaInfo.num_items,
    });
    try { localStorage.setItem(key, "1"); } catch { /* noop */ }
  }, [order, metaInfo]);

  // "Riprova": crea un NUOVO checkout SumUp per lo stesso ordine e reindirizza.
  const handleRetry = async () => {
    if (!orderNumber) return;
    setRetrying(true);
    try {
      const res = await createOrderCheckout(orderNumber);
      if (res.already_paid) { window.location.reload(); return; }
      if (!res.hosted_checkout_url) throw new Error("no url");
      window.location.href = res.hosted_checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile riavviare il pagamento. Riprova.");
      setRetrying(false);
    }
  };

  if (error) {
    return (
      <div className="container-page section" data-testid="confirm-error">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">Ordine non trovato.</h1>
        <p className="mt-3 text-muted-foreground">Controlla il link ricevuto oppure contattaci.</p>
        <Button asChild variant="lime" className="mt-8 rounded-full"><Link to="/">Torna alla home</Link></Button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-page section" data-testid="confirm-loading">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin text-lime" /> Verifica del pagamento in corso…
        </div>
      </div>
    );
  }

  const status = order.payment_status;
  // "Pagato" SOLO se lo conferma il DB (aggiornato da SumUp via webhook/recheck).
  const paid = status === "paid";
  const failed = !paid && (status === "failed" || status === "expired" || status === "mismatch");
  const pending = !paid && !failed;

  const head = paid
    ? { Icon: CheckCircle2, tint: "text-ink", bg: "bg-lime", eyebrow: "Pagamento riuscito", title: `Grazie ${order.customer.full_name.split(" ")[0]}, ordine confermato.`, sub: "Abbiamo ricevuto il pagamento. Riceverai a breve l'email di conferma con i dettagli della spedizione." }
    : failed
    ? { Icon: XCircle, tint: "text-white", bg: "bg-destructive", eyebrow: status === "expired" ? "Sessione scaduta" : "Pagamento non riuscito", title: status === "expired" ? "La sessione di pagamento è scaduta." : "Il pagamento non è andato a buon fine.", sub: "Nessun importo è stato addebitato. Puoi riprovare subito con un nuovo pagamento." }
    : { Icon: Clock, tint: "text-ink", bg: "bg-lime", eyebrow: "In attesa di pagamento", title: "Stiamo confermando il pagamento…", sub: "Appena SumUp conferma, questo stato si aggiorna da solo. Puoi lasciare aperta questa pagina." };

  const HeadIcon = head.Icon;

  return (
    <div className="container-page section" data-testid="order-confirmation">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
        <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE_OUT }}>
          <div className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${head.bg} ${head.tint}`}>
            {pending ? <HeadIcon className="h-5 w-5 animate-pulse" strokeWidth={2.5} /> : <HeadIcon className="h-5 w-5" strokeWidth={2.5} />}
          </div>
          <div className="mt-8 text-sm font-semibold uppercase tracking-wide text-lime" data-testid="confirm-status">{head.eyebrow}</div>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl">{head.title}</h1>
          <p className="mt-5 max-w-xl text-lg text-muted-foreground">{head.sub}</p>

          <div className="mt-10 inline-flex flex-col gap-1 rounded-2xl bg-card px-6 py-5 ring-1 ring-white/5" data-testid="confirm-order-box">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Numero ordine</span>
            <span className="font-display text-2xl font-extrabold tracking-tight text-lime" data-testid="order-number">{order.order_number}</span>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {paid && (
              <Button asChild variant="lime" size="lg" className="rounded-full" data-testid="confirm-continue">
                <Link to="/catalogo">Continua lo shopping <ArrowRight strokeWidth={2} /></Link>
              </Button>
            )}
            {failed && (
              <Button variant="lime" size="lg" className="rounded-full" onClick={handleRetry} disabled={retrying} data-testid="confirm-retry">
                {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {retrying ? "Riavvio pagamento" : "Riprova"} {!retrying && <ArrowRight strokeWidth={2} />}
              </Button>
            )}
            {pending && (
              <Button variant="outline" size="lg" className="rounded-full" onClick={() => window.location.reload()} data-testid="confirm-refresh">
                <RefreshCw className="h-4 w-4" strokeWidth={2} /> Aggiorna stato
              </Button>
            )}
          </div>
        </motion.div>

        <motion.aside
          className="rounded-2xl bg-card p-6 ring-1 ring-white/5 lg:col-span-5 lg:self-start"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE_OUT, delay: 0.15 }}
          data-testid="order-summary"
        >
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Riepilogo</div>
          <ul className="mt-5 space-y-4">
            {order.items.map((item, i) => (
              <li key={i} className="flex items-start justify-between gap-4 text-sm">
                <div>
                  <div className="font-semibold text-foreground">{item.name}</div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{item.brand}</span>
                    <GradeBadge grade={item.grade} compact />
                    <span>×{item.quantity}</span>
                  </div>
                </div>
                <div className="whitespace-nowrap font-semibold text-foreground">{formatPrice(item.unit_price * item.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-end justify-between pt-5 [border-top:1px_solid_hsl(var(--border))]">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Totale</span>
            <span className="font-display text-2xl font-extrabold text-foreground" data-testid="order-total">{formatPrice(order.total)}</span>
          </div>
        </motion.aside>
      </div>
    </div>
  );
}
